<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'

import UiHeader from '@/components/UiHeader/UiHeader.vue'
import UiHero from '@/components/UiHero/UiHero.vue'
import UiAntForm from '@/components/UiForm/UiAntForm.vue'
import UiFooter from '@/components/UiFooter/UiFooter.vue'
import PartnerOfferCard from '@/components/PartnerOfferCard/PartnerOfferCard.vue'
import PartnerTermCard from '@/components/PartnerTermCard/PartnerTermCard.vue'
import type { PartnerOfferTerm } from '@/types'

interface LegalEntity {
  name: string | null
  inn: string | null
  ogrn: string | null
  offer: string | null
}

interface Showcase {
  product_name: string
  price: number
  offers_count: number
  legal_entity: LegalEntity | null
  show_accept_all_terms?: boolean
}

interface Offer {
  id: string
  product_name: string
  description: string | null
  logo_url: string | null
  sort_order: number
}

const route = useRoute()

// Витрина по конкретному продукту: /l/showcase/{draftInvoiceId}.
// Без параметра — общая витрина по всем оферам тенанта.
const draftInvoiceId = computed(() => (route.params.draftInvoiceId as string) || null)
const showcaseBase = computed(() =>
  draftInvoiceId.value ? `/l/showcase/${draftInvoiceId.value}` : '/l/showcase'
)

const unavailable = ref<boolean>(false)
const showcase = ref<Showcase | null>(null)
const pageLoader = ref(true)

const offers = ref<Offer[]>([])
const terms = ref<PartnerOfferTerm[]>([])
const requestId = ref<string | null>(null)
const showTerms = computed(() => terms.value.length > 0)
const showOffers = computed(() => !showTerms.value && offers.value.length > 0)

const submitUrl = computed(() => `${showcaseBase.value}/submit`)

async function getShowcase() {
  try {
    const response = await axios.get<Showcase>(showcaseBase.value)
    showcase.value = response.data
  } catch (error) {
    unavailable.value = true
  }
}

const offerUrls = computed<string[]>(() => {
  const offer = showcase.value?.legal_entity?.offer
  return offer ? [offer] : []
})

type NextStep = { redirect_url?: string; request_id?: string; offers?: Offer[]; terms?: PartnerOfferTerm[] }

// Ответ сабмита: один офер → { redirect_url } или { terms } (несколько сроков);
// несколько оферов → { request_id, offers }.
function onSubmitSuccess(data: NextStep) {
  if (data.redirect_url) {
    window.location.href = data.redirect_url
    return
  }

  if (data.request_id) {
    requestId.value = data.request_id
  }

  if (data.terms?.length) {
    terms.value = [...data.terms].sort((a, b) => a.sort_order - b.sort_order || a.term_months - b.term_months)
    return
  }

  if (data.offers?.length) {
    offers.value = [...data.offers].sort((a, b) => a.sort_order - b.sort_order)
  }
}

async function chooseOffer(offer: Offer) {
  try {
    const response = await axios.post<NextStep>('/l/choose', {
      request_id: requestId.value,
      offer_id: offer.id
    })
    onSubmitSuccess(response.data)
  } catch (error) {
    unavailable.value = true
  }
}

async function chooseTerm(term: PartnerOfferTerm) {
  try {
    const response = await axios.post<{ redirect_url: string }>('/l/choose-term', {
      request_id: requestId.value,
      term_id: term.id
    })
    window.location.href = response.data.redirect_url
  } catch (error) {
    unavailable.value = true
  }
}

onMounted(async () => {
  try {
    await getShowcase()
  } finally {
    pageLoader.value = false
  }
})
</script>

<template>
  <div v-if="pageLoader || showcase" class="payment-cabinet">
    <div>
      <ui-header :logo-loading="pageLoader" />

      <template v-if="showcase">
        <!-- Выбор срока внутри офера -->
        <template v-if="showTerms">
          <ui-hero
            :title="showcase.product_name"
            :subtitle="`${showcase.price} ₽`"
          />

          <div class="partner-link-showcase container">
            <h2 class="partner-link-showcase__title">Выберите комфортный срок и платёж для вашей рассрочки</h2>
            <p class="partner-link-showcase__subtitle">После выбора срока вы перейдёте к заполнению основной анкеты. Обычно это занимает не более 3-5 минут.</p>

            <PartnerTermCard
              v-for="term in terms"
              :key="term.id"
              :term-months="term.term_months"
              :monthly-payment="term.monthly_payment"
              @select="chooseTerm(term)"
            />
          </div>
        </template>

        <!-- Витрина выбора офера -->
        <template v-else-if="showOffers">
          <ui-hero title="Выберите предложение" />

          <div class="partner-link-showcase container">
            <PartnerOfferCard
              v-for="offer in offers"
              :key="offer.id"
              :title="offer.product_name"
              :description="offer.description ?? undefined"
              :logo-url="offer.logo_url ?? undefined"
              @select="chooseOffer(offer)"
            />
          </div>
        </template>

        <!-- Форма с данными и согласиями -->
        <template v-else>
          <ui-hero
            :title="showcase.product_name"
            :subtitle="`${showcase.price} ₽`"
          />

          <ui-ant-form
            :form-options="null"
            :offer-urls="offerUrls"
            :show-accept-all="showcase.show_accept_all_terms"
            :submit-url="submitUrl"
            redirect-field="redirect_url"
            :on-submit-success="onSubmitSuccess"
          />
        </template>

        <ui-footer />
      </template>
    </div>
  </div>

  <div v-if="unavailable" class="payment-cabinet">
    <div class="container">
      <ui-hero title="Страница недоступна" />
    </div>
  </div>
</template>

<style lang="scss">
.partner-link-showcase {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
  }

  &__subtitle {
    margin: 0 0 8px;
    font-size: 14px;
    color: #6b7280;
  }
}
</style>
